import React, { useEffect, useState } from 'react';
import { ImageOff, RefreshCw, Loader2, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

/**
 * Admin: scans the DB for items whose cover_image points to a local file
 * that doesn't exist on disk. Helps catch stragglers from the old buggy
 * upload endpoint that never wrote files.
 */
const CoverAudit = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setRefreshing(true);
    setError('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/cover-audit`);
      const json = await r.json();
      if (!r.ok) throw new Error(json.detail || 'Audit failed');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="cover-audit">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <ImageOff className="w-5 h-5 text-amber-300" />
            Cover audit
          </CardTitle>
          <p className="text-xs text-gray-400 mt-1">
            Finds content rows whose cover image references a local file that's
            missing on disk. External URLs (https://…) are assumed reachable.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={refreshing}
          className="border-slate-700 text-gray-200 hover:bg-slate-700"
          data-testid="cover-audit-refresh"
        >
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
          )}
          Re-scan
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-400 text-sm">Scanning…</p>
        ) : error ? (
          <p className="text-red-400 text-sm">{error}</p>
        ) : !data ? (
          <p className="text-gray-400 text-sm">No data.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label="Items scanned" value={data.scanned} />
              <Stat label="Covers OK" value={data.ok_count} tone="emerald" />
              <Stat
                label="Missing on disk"
                value={data.missing_count}
                tone={data.missing_count ? 'red' : 'emerald'}
              />
            </div>

            {data.missing_count === 0 ? (
              <div
                className="flex items-center gap-2 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm"
                data-testid="cover-audit-clean"
              >
                <CheckCircle2 className="w-5 h-5" />
                All covers are healthy. Nothing to fix.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-1 rounded-lg border border-slate-700/60">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-800/80 text-left text-xs uppercase tracking-wider text-gray-400">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Title</th>
                      <th className="px-3 py-2 font-semibold">Kind</th>
                      <th className="px-3 py-2 font-semibold">Owner</th>
                      <th className="px-3 py-2 font-semibold">Cover URL</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/60 text-gray-200">
                    {(data.missing || []).map((m) => (
                      <tr
                        key={`${m.collection}-${m.id}`}
                        className="hover:bg-slate-800/40"
                        data-testid={`cover-audit-row-${m.id}`}
                      >
                        <td className="px-3 py-2 max-w-[260px]">
                          <span className="font-semibold text-white truncate block">
                            {m.title || '(untitled)'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-300 text-xs">
                          {m.kind}
                        </td>
                        <td className="px-3 py-2 text-gray-300 text-xs">
                          {m.owner_email || (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <a
                            href={m.cover_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 break-all"
                            title={m.expected_disk_path}
                          >
                            {m.cover_url}
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-200 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
                            <AlertTriangle className="w-3 h-3" />
                            Missing
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-gray-500 px-3 py-2">
                  Tip — to fix one of these, open the item in the Library and
                  re-upload the cover. The new upload endpoint writes to disk
                  correctly so the image will start displaying immediately.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate: 'text-white',
    emerald: 'text-emerald-300',
    red: 'text-red-300',
  };
  return (
    <div className="bg-slate-900/60 border border-slate-700/60 rounded-lg px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-widest text-gray-400">
        {label}
      </p>
      <p className={`text-2xl font-bold mt-0.5 ${tones[tone] || tones.slate}`}>
        {value ?? 0}
      </p>
    </div>
  );
};

export default CoverAudit;
